import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { DominoLogo } from '@/components/ui/DominoLogo';
import { cn } from '@/lib/utils';

/** Online diagnostic: one question per screen, large cards (in the spirit of Pretto). */

export const diagnosticPrimary =
  'min-h-[52px] rounded-full bg-domino-aubergine px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-domino-prune disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-domino-prune';

export const DiagnosticLayout = ({ progress, children }: { progress: number | null; children: ReactNode }) => (
  <div className="flex min-h-screen flex-col bg-domino-ivory font-sans text-domino-ink">
    <header className="flex items-center justify-between px-4 py-4 md:px-10">
      <a href="https://dominoflow.com" className="flex items-center gap-2.5 font-display text-xl font-semibold text-domino-aubergine">
        <DominoLogo tile className="h-8 w-8" /> Domino
      </a>
    </header>
    {progress !== null && (
      <div className="h-1.5 w-full bg-domino-muted" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
        <div className="h-full bg-domino-aubergine transition-[width] duration-300" style={{ width: `${Math.round(progress * 100)}%` }} />
      </div>
    )}
    <main className="mx-auto flex w-full max-w-[680px] flex-1 flex-col gap-8 px-4 py-10 md:py-16">{children}</main>
  </div>
);

export const QuestionTitle = ({ children }: { children: ReactNode }) => (
  <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-domino-aubergine md:text-4xl">{children}</h1>
);

export const SubQuestion = ({ children }: { children: ReactNode }) => (
  <h2 className="text-base font-semibold text-domino-ink">{children}</h2>
);

export const ChoiceGrid = <K extends string | boolean>(props: {
  options: ReadonlyArray<{ key: K; label: string }>;
  isSelected: (key: K) => boolean;
  onPick: (key: K) => void;
  multiple?: boolean;
  columns?: 1 | 2;
}) => (
  <div className={cn('grid gap-3', props.columns === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2')} role={props.multiple ? 'group' : 'radiogroup'}>
    {props.options.map((option) => {
      const on = props.isSelected(option.key);
      return (
        <button
          key={String(option.key)}
          type="button"
          role={props.multiple ? 'checkbox' : 'radio'}
          aria-checked={on}
          onClick={() => props.onPick(option.key)}
          className={cn(
            'flex min-h-[60px] items-center justify-between gap-3 rounded-2xl border-2 bg-white px-5 py-4 text-left text-[16px] font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-domino-prune',
            on ? 'border-domino-aubergine bg-domino-lilac text-domino-aubergine' : 'border-domino-sand text-domino-ink hover:border-domino-aubergine/40',
          )}
        >
          <span>{option.label}</span>
          <span
            aria-hidden
            className={cn(
              'flex h-6 w-6 flex-none items-center justify-center border-2',
              props.multiple ? 'rounded-md' : 'rounded-full',
              on ? 'border-domino-aubergine bg-domino-aubergine text-white' : 'border-domino-sand',
            )}
          >
            {on && (
              <svg width="12" height="12" viewBox="0 0 12 12"><path d="M2.5 6.5l2.2 2.2 4.8-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

export const DiagnosticTextArea = (props: {
  id: string; label: string; value: string; onChange: (value: string) => void; maxLength: number; rows?: number;
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={props.id} className="text-base font-semibold text-domino-ink">{props.label}</label>
    <textarea
      id={props.id}
      rows={props.rows ?? 3}
      value={props.value}
      maxLength={props.maxLength}
      onChange={(event) => props.onChange(event.target.value)}
      className="rounded-2xl border-2 border-domino-sand bg-white px-4 py-3.5 text-base text-domino-ink focus-visible:border-domino-aubergine focus-visible:outline-none"
    />
  </div>
);

/** A free text of one line that grows with what is written; its label is for screen readers only. */
export const DiagnosticGrowingText = (props: { label: string; value: string; onChange: (value: string) => void; maxLength: number }) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const field = ref.current;
    if (!field) return;
    field.style.height = 'auto';
    field.style.height = `${field.scrollHeight}px`;
  }, [props.value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      aria-label={props.label}
      placeholder="Autre chose…"
      value={props.value}
      maxLength={props.maxLength}
      onChange={(event) => props.onChange(event.target.value)}
      className="resize-none overflow-hidden rounded-2xl border-2 border-domino-sand bg-white px-4 py-3 text-base text-domino-ink placeholder:text-domino-inkSoft/70 focus-visible:border-domino-aubergine focus-visible:outline-none"
    />
  );
};

export const DiagnosticInput = (props: {
  id: string; label: string; value: string; onChange: (value: string) => void;
  type?: string; placeholder?: string; autoComplete?: string; inputMode?: 'email' | 'numeric' | 'url';
}) => (
  <div className="flex flex-col gap-2">
    <label htmlFor={props.id} className="text-base font-semibold text-domino-ink">{props.label}</label>
    <input
      id={props.id}
      type={props.type ?? 'text'}
      value={props.value}
      inputMode={props.inputMode}
      placeholder={props.placeholder}
      autoComplete={props.autoComplete}
      onChange={(event) => props.onChange(event.target.value)}
      className="min-h-[56px] rounded-2xl border-2 border-domino-sand bg-white px-4 py-3.5 text-lg text-domino-ink focus-visible:border-domino-aubergine focus-visible:outline-none"
    />
  </div>
);

export const StepNav = (props: { onBack: (() => void) | null; onNext: () => void; nextLabel: string; nextDisabled: boolean; busy?: boolean }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
    {props.onBack ? (
      <button type="button" onClick={props.onBack} className="min-h-[44px] px-1 text-base font-semibold text-domino-inkSoft hover:text-domino-aubergine">
        ← Retour
      </button>
    ) : <span />}
    <button type="button" onClick={props.onNext} disabled={props.nextDisabled || props.busy} className={diagnosticPrimary}>
      {props.nextLabel}
    </button>
  </div>
);

export const DiagnosticAlert = ({ children }: { children: ReactNode }) => (
  <p role="alert" className="rounded-2xl bg-domino-muted px-4 py-3 text-[15px] font-semibold text-domino-aubergine">{children}</p>
);
